/** @jsxImportSource @emotion/react */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import TextButton from '@design-system/text-button';
import TextBubble from '@design-system/text-bubble';
import { IMiniStoryToastData } from '@datatype/iyagigo/type';
import { ETheme } from '@datatype/type';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as classroomActions } from '@store/slice/classroom-slice';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useNavigate } from 'react-router-dom';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { EApiStoryType } from '@datatype/iyagigo/api-type';

import { useStoriesQuery } from '@hook/iyagigo/useApi';
import { useInfiniteScroll } from '@hook/common/useScroll';
import { v4 as uuid4 } from 'uuid';

interface IProps {
  data: IMiniStoryToastData['data'];
}

const Wrapper = styled.div`
  position: relative;
  height: ${size.TOAST_HEIGHT};
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
`;

const StoriesContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
  width: 100%;
`;

const StoryItemContainer = styled.div<{ $selected?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  border-radius: 5px;
  border: 1px solid ${(props) => (props.$selected ? color.BROWN_2 : color.GRAY_BOX_BORDER_COLOR)};
  padding: 10px 10px;
  gap: ${size.MARGIN_1};
  width: 100%;
  cursor: pointer;
`;

const StoryItemDescriptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex: 1;
  min-width: 0;
  width: 100%;
  gap: 5px;
`;

const MiniStoryToast: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();

  const storyQuery = useStoriesQuery(data.languageCode, EApiStoryType.MINI_STORY, data.releaseDate);
  // 첫 번째 아이템은 clip-home의 "오늘의 이야기" 카드와 동일하므로 제외
  const stories = useMemo(
    () => (storyQuery.data?.pages.flatMap((page) => page.data ?? []) ?? []).slice(1),
    [storyQuery.data],
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: storyQuery.hasNextPage ?? false,
    isFetchingNextPage: storyQuery.isFetchingNextPage ?? false,
    fetchNextPage: storyQuery.fetchNextPage,
    root: scrollContainerRef,
  });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    if (selectedKey === null && stories[0]) {
      setSelectedKey(stories[0].key);
    }
  }, [stories, selectedKey]);

  const handleSelect = (key: string) => {
    setSelectedKey(key);
  };

  const handleStudyClick = () => {
    if (!selectedKey) return;
    dispatch(classroomActions.storeCurrentStoryKey(selectedKey));
    dispatch(modalActions.hideToast());
    navigate(IYAGIGO_PATH.CLIP_MINI_STORY_PATH);
  };

  const Stories = stories.map((story) => {
    const isSelected = selectedKey === story.key;
    const situation = story.situationKo ?? '';
    const firstLine = story.sourceLine?.[0];
    const firstEmoji = '🦦';

    return (
      <StoryItemContainer key={uuid4()} $selected={isSelected} onClick={() => handleSelect(story.key)}>
        <StoryItemDescriptionContainer>
          <TextBlock
            text={situation}
            design={{
              text: { size: 'medium', color: color.BLACK_2, bold: isSelected, oneline: true },
              box: { alignX: 'left' },
            }}
          />
          {firstLine && (
            <TextBubble
              type='plain'
              text={firstLine.sentence ?? ''}
              align='left'
              theme={ETheme.GRAY}
              size='small'
              profileEmoji={firstEmoji}
              lineMaxWidth='calc(100%)'
              oneline={true}
            />
          )}
        </StoryItemDescriptionContainer>
      </StoryItemContainer>
    );
  });

  return (
    <Wrapper>
      {/*<TextBlock*/}
      {/*  text='이야기 더보기'*/}
      {/*  design={{*/}
      {/*    text: { size: 'large', color: color.BLACK_2, bold: true },*/}
      {/*    box: { alignX: 'left', width: '100%' },*/}
      {/*  }}*/}
      {/*/>*/}
      {/*<Gap height={'5px'} />*/}
      <ScrollContainer>
        <Scroll direction='y' height='100%' containerRef={scrollContainerRef}>
          <StoriesContainer>
            {Stories}
            {/* 무한스크롤 sentinel — viewport 진입 시 다음 페이지 자동 fetch */}
            {stories.length > 0 && <div ref={sentinelRef} style={{ width: '100%', height: '1px' }} />}
          </StoriesContainer>
        </Scroll>
      </ScrollContainer>
      <Gap height={size.MARGIN_1} />
      <TextButton
        text={'학습하기'}
        theme={ETheme.BROWN}
        size={'medium'}
        shape='pill'
        width={'100%'}
        handleClick={handleStudyClick}
        disabled={!selectedKey}
      />
    </Wrapper>
  );
};

export default MiniStoryToast;
